import React, { useContext, useState, useEffect, useCallback } from "react";
import { CheckCircle, XCircle, Loader2 } from "lucide-react"; // تأكد من استدعاء الأيقونات

import {
  CalendarDays,
  Users,
  MapPin,
  Clock,
  QrCode,
  Edit,
  Trash2,
  ArrowLeft,
  User,
  AlertCircle,
  Check,
  RefreshCw,
  Share2,
} from "lucide-react";
import ChatbotButton from "../../components/common/ChatbotButton";
import { useLocation, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/NewActivityDetails.css";
import ConfirmRegistrationModal from "../../components/common/ConfirmActivityModal";
import { userRoles } from "../../App";
import {
  approveActivityAPI,
  cancelRegistrationAPI,
  evaluateAPI,
  recordAttendanceAPI,
  RegisterUserActivity,
  rejectActivityAPI,
} from "../../services/userService";
import ConfirmAttendanceModal from "../../components/common/ConfirmAttendanceModal";
import { AuthContext } from "../../context/AuthContext";
import EvaluateModal from "../../components/common/EvaluateModal";
import { ActivityDetailsAPI } from "../../services/activityService";
import { FaUsers } from "react-icons/fa";
import PopupMessage from "../../components/common/PopupMessage";
import ConfirmCancelRegistrationModal from "../../components/common/ConfirmCancelRegistrationModal";
import AdminTargetInfo from "../../components/AdminTargetInfo";
import ActivityFeedback from "../../components/ActivityFeedback";

/**
 * ActivityDetails component
 * - Fetches activity data from ActivityDetailsAPI using an activity id
 * - Shows loader, error state and allows manual retry
 * - Normalizes backend shape to the UI-friendly shape used by the existing JSX
 * - UI: improved layout with countdowns (registration & activity) + responsive + Bootstrap + custom CSS
 *
 * IMPORTANT: I did NOT remove any existing logic or buttons — only enhanced UI and added timers.
 */
export default function ActivityDetails() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [raw, setRaw] = useState(null); // raw backend object
  const [data, setData] = useState(null); // normalized UI object
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [show, setShow] = useState(false);
  const [showEvaluateModal, setShowEvaluateModal] = useState(false);
  const [submittingEval, setSubmittingEval] = useState(false);
  const [evalMessage, setEvalMessage] = useState("");
  const [evalMessageType, setEvalMessageType] = useState("");
  const [ShowCancelModal, setShowCancelModal] = useState(false);
  const [adminPayload, setAdminPayload] = useState(null);
  const [isBanned, setIsBanned] = useState(false);
  const [feedbackAnalytics, setFeedback] = useState(null);
  const [popup, setPopup] = useState({ show: false, type: "", message: "" });

  /* **************************************** */

  // import { approveActivityAPI, rejectActivityAPI } from "../../services/activityService"; // استدعي الـ APIs بتاعتك

  // ... inside your component function

  // 1. State للتحكم في المودال
  const [modalConfig, setModalConfig] = useState({
    show: false,
    id: null,
    type: null, // 'approve' أو 'reject'
  });
  const [actionLoading, setActionLoading] = useState(false);

  // 2. دوال الفتح (بدل الـ window.confirm القديمة)
  const handleAccept = (id) => {
    setModalConfig({ show: true, id, type: "approve" });
  };

  const handleReject = (id) => {
    setModalConfig({ show: true, id, type: "reject" });
  };

  // 3. دالة التنفيذ الفعلية (لما يدوس نعم في المودال)
  const executeAction = async () => {
    if (!modalConfig.id) return;

    setActionLoading(true);
    try {
      if (modalConfig.type === "approve") {
        //  نداء الـ API للموافقة
        await approveActivityAPI(modalConfig.id);
        setPopup({
          show: true,
          type: "success",
          message: "Activity accepted successfully!",
        });
        setTimeout(() => {
          navigate(-1);
        }, 3000);
        // alert("Activity accepted successfully!"); // يفضل استبدالها بـ Toast
      } else {
        //  نداء الـ API للرفض
        await rejectActivityAPI(modalConfig.id);
        setPopup({
          show: true,
          type: "success",
          message: "Activity rejected successfully!",
        });
        setTimeout(() => {
          navigate(-1);
        }, 3000);
      }

      // تحديث الليستة (عشان النشاط يختفي أو حالته تتغير)
      // fetchActivities(); // لو عندك دالة بتجيب الداتا تاني استدعيها هنا

      // قفل المودال
      setModalConfig({ show: false, id: null, type: null });
    } catch (error) {
      console.error(error);
      setPopup({
        show: true,
        type: "error",
        message: error?.message || "Something went wrong!",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // 4. دالة الغلق
  const closeModal = () => {
    setModalConfig({ show: false, id: null, type: null });
  };

  /* **************************************** */

  // countdown state (ms left)
  const [countdowns, setCountdowns] = useState({
    toOpen: null,
    toClose: null,
    toStart: null,
    toEnd: null,
    now: new Date().getTime(),
  });

  // try to find activity id from several likely places (location.state, query, or last path segment)
  const getActivityIdFromLocation = useCallback(() => {
    if (location?.state) {
      // supports: { activity: { _id } }, { _id }, { id }
      const st = location.state;
      if (st.activity && (st.activity._id || st.activity.id))
        return st.activity._id || st.activity.id;
      if (st._id || st.id) return st._id || st.id;
    }

    // try query param ?id=...
    const qs = new URLSearchParams(location.search);
    const qId = qs.get("id");
    if (qId) return qId;

    // fallback: try to parse a 24-char hex id from the pathname
    const m =
      location.pathname && location.pathname.match(/([a-fA-F0-9]{24})(?:\/)?$/);
    if (m) return m[1];

    return null;
  }, [location]);

  const activityId = getActivityIdFromLocation();

  const normalize = useCallback(
    (rawObj) => {
      if (!rawObj) return null;

      const id = rawObj._id || rawObj.id;
      const poster = rawObj.posterImage || rawObj.poster || rawObj.image;
      const title = rawObj.activityName || rawObj.title || "Untitled Activity";
      const description = rawObj.description || "";
      const instructions =
        rawObj.specialInstructions || rawObj.instructions || "";
      const capacity = Number(rawObj.capacity) || 0;
      const participantsArr = Array.isArray(rawObj.participants)
        ? rawObj.participants
        : [];

      const registered =
        typeof rawObj.participantsCount === "number"
          ? rawObj.participantsCount
          : participantsArr.length;

      const percentage =
        capacity > 0 ? Math.round((registered / capacity) * 100) : 0;

      const isRegistered = rawObj.isRegistered ?? false;
      const isOwner =
        rawObj.isOwner ??
        (rawObj.supervisorID && user && rawObj.supervisorID === user?._id);

      const type = rawObj.activityType || rawObj.type || "UNKNOWN";
      const status = rawObj.status || rawObj.activityStatus || "ACTIVE";

      const coordinates = Array.isArray(rawObj.coordinates)
        ? rawObj.coordinates
        : [];

      const supervisor = rawObj.supervisorName || rawObj.supervisor || null;

      const links = Array.isArray(rawObj.links) ? rawObj.links : [];

      const attendance = Array.isArray(rawObj.attendance)
        ? rawObj.attendance
        : [];

      const feedbacks = Array.isArray(rawObj.feedbacks) ? rawObj.feedbacks : [];

      const registration = rawObj.registration || {
        openAt: null,
        closeAt: null,
      };
      const activitySchedule = rawObj.activitySchedule || {
        startAt: null,
        endAt: null,
      };

      const attendanceOTP =
        rawObj.attendanceOTP ?? rawObj.attendanceCode ?? null;

      const isFull = rawObj.isFull ?? (capacity > 0 && registered >= capacity);

      return {
        id,
        image: poster,
        poster,
        title,
        registered,
        capacity,
        percentage,
        role: user?.role || null,
        isRegistered,
        isOwner,
        description,
        loc: rawObj.locationName || null,
        supervisor,
        instructions,
        type,
        AcceptanceStatus: rawObj.status === "ACTIVE" ? true : false,
        coordinates,
        links,
        status,
        attendanceOTP,
        attendance,
        activitySchedule,
        registration,
        participants: participantsArr,
        feedbacks,
        isFull,
      };
    },
    [user],
  );

  // fetcher
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function fetchActivity() {
      setLoading(true);
      setError(null);

      try {
        if (!activityId) {
          throw new Error("Activity id not found in URL or location state.");
        }

        const res = await ActivityDetailsAPI(activityId, {
          signal: controller.signal,
        });
        setAdminPayload(res.InfoForAdmin);
        setFeedback(res?.feedbackAnalytics);

        // backend might return { success: true, activity: { ... } } or { message: { ... } } etc.
        setIsBanned(res?.activity?.isBanned);

        const payload = res?.activity ?? res?.message ?? res?.data ?? res;

        if (cancelled) return;

        setRaw(payload);

        setData(normalize(payload));
      } catch (err) {
        if (err.name === "AbortError") return; // ignore abort
        console.error("Failed loading activity:", err);
        setError(err.message || "Failed to load activity");
        setRaw(null);
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchActivity();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [activityId, normalize]);

  const refetch = () => {
    setLoading(true);
    setError(null);
    // re-run effect by changing state: simplest is to call ActivityDetailsAPI directly
    (async () => {
      try {
        if (!activityId) throw new Error("Activity id not found.");
        const res = await ActivityDetailsAPI(activityId);
        const payload = res?.activity ?? res?.message ?? res?.data ?? res;
        setRaw(payload);
        setData(normalize(payload));
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load activity");
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleRegister = () => {
    if (data?.isFull) return;
    setShow(true);
  };

  const handleConfirm = async () => {
    try {
      const res = await RegisterUserActivity(data.id);
      if (res) {
        setShow(false);
        setPopup({
          show: true,
          type: "success",
          message: "You have registered successfully!",
        });
        setTimeout(() => {
          navigate(-1);
        }, 3000);
      }
    } catch (error) {
      setPopup({
        show: true,
        type: "error",
        message: error.message || "You have registered successfully!",
      });
      setShow(false);
    }
  };

  const handleEdit = () =>
    navigate(`/activities/categories/${data.type}/edit`, {
      state: { activity: raw },
    });

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this activity?")) {
      alert("🗑️ Activity deleted successfully!");
    }
  };

  const handleOpenMap = (lat, lng) => {
    const mapUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(mapUrl, "_blank");
  };

  const handleEvaluateSubmit = async ({ rating, comment }) => {
    try {
      setSubmittingEval(true);
      setEvalMessage("");
      setEvalMessageType("");

      await evaluateAPI(data.id, rating, comment);

      setEvalMessage("Your evaluation has been submitted successfully!");
      setEvalMessageType("success");

      setTimeout(() => setShowEvaluateModal(false), 1500);
      // optionally refetch to update feedbacks
      refetch();
    } catch (error) {
      setEvalMessage(error.message || "Failed to submit evaluation.");
      setEvalMessageType("error");
    } finally {
      setSubmittingEval(false);
    }
  };

  const sendOTP = async (otpCode) => {
    try {
      const result = await recordAttendanceAPI(otpCode, data.id);
      return { success: true, message: result.attendance };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  // TIMERS: update countdowns each second based on registration & activitySchedule
  useEffect(() => {
    let timerId = null;

    function compute() {
      const now = Date.now();
      const reg = data?.registration ?? {};
      const sched = data?.activitySchedule ?? {};

      const toOpen = reg.openAt ? new Date(reg.openAt).getTime() - now : null;
      const toClose = reg.closeAt
        ? new Date(reg.closeAt).getTime() - now
        : null;
      const toStart = sched.startAt
        ? new Date(sched.startAt).getTime() - now
        : null;
      const toEnd = sched.endAt ? new Date(sched.endAt).getTime() - now : null;

      setCountdowns({
        toOpen: toOpen,
        toClose: toClose,
        toStart: toStart,
        toEnd: toEnd,
        now,
      });
    }

    // compute now and every second
    compute();
    timerId = setInterval(compute, 1000);

    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [data]);

  // helper: format ms to "Xd Yh Zm Ss" (nearest)
  const formatDuration = (ms) => {
    if (ms === null || ms === undefined) return null;
    if (ms <= 0) return "0s";

    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  // build friendly countdown messages
  const regMessage = (() => {
    if (!data) return null;
    const { registration } = data;
    const { toOpen, toClose } = countdowns;
    if (!registration || (!registration.openAt && !registration.closeAt))
      return null;

    if (toOpen !== null && toOpen > 0) {
      return {
        text: `Registration opens in ${formatDuration(toOpen)}`,
        type: "info",
      };
    }
    if (toClose !== null && toClose > 0) {
      return {
        text: `Registration closes in ${formatDuration(toClose)}`,
        type: "warning",
      };
    }
    // closed (either closeAt passed or closeAt missing & now >= openAt)
    return { text: `Registration closed`, type: "muted" };
  })();

  const activityMessage = (() => {
    if (!data) return null;
    const { activitySchedule } = data;
    const { toStart, toEnd } = countdowns;
    if (
      !activitySchedule ||
      (!activitySchedule.startAt && !activitySchedule.endAt)
    )
      return null;

    if (toStart !== null && toStart > 0) {
      return {
        text: `Activity starts in ${formatDuration(toStart)}`,
        type: "info",
      };
    }
    if (toEnd !== null && toEnd > 0) {
      return {
        text: `Activity ends in ${formatDuration(toEnd)}`,
        type: "success",
      };
    }
    return { text: `Activity finished`, type: "muted" };
  })();

  // if loading show spinner / skeleton
  if (loading) {
    return (
      <div className="container mt-5 pt-5 text-center">
        <div
          className="d-flex flex-column align-items-center justify-content-center"
          style={{ minHeight: "40vh" }}
        >
          <div
            className="spinner-border"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading activity details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5 pt-5 text-center">
        <h4 className="text-danger">⚠️ {error}</h4>
        <div className="d-flex gap-2 justify-content-center mt-3">
          <button
            className="btn btn-primary"
            onClick={() => navigate("/auth/login")}
          >
            <RefreshCw size={16} className="me-1" /> Login
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate("/")}
          >
            <ArrowLeft size={16} className="me-1" /> Back
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mt-5 pt-5 text-center">
        <h4 className="text-secondary">No activity data available.</h4>
        <button
          className="btn btn-outline-secondary mt-3"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} className="me-1" /> Back
        </button>
      </div>
    );
  }

  // destructure for rendering (unchanged)
  const {
    id,
    image,
    title,
    registered,
    capacity,
    percentage,
    role,
    isRegistered = false,
    isOwner = false,
    description,
    loc,
    supervisor,
    instructions,
    type,
    poster,
    coordinates = [],
    links = [],
    status,
    attendanceOTP,
    attendance = [],
    activitySchedule = {},
    registration = {},
    participants = [],
    feedbacks = [],
    isFull,
  } = data;

  // Safety defaults for dates
  const now = new Date();
  const openAt = registration.openAt ? new Date(registration.openAt) : null;
  const closeAt = registration.closeAt ? new Date(registration.closeAt) : null;
  const startAt = activitySchedule.startAt
    ? new Date(activitySchedule.startAt)
    : null;
  const endAt = activitySchedule.endAt
    ? new Date(activitySchedule.endAt)
    : null;

  let uiState = "";

  if (startAt && endAt) {
    if (now >= startAt) {
      if (now > endAt) {
        uiState = "activity_finished";
      } else {
        uiState = "activity_started";
      }
    } else {
      if (!openAt) uiState = "registration_not_open";
      else if (now < openAt) uiState = "registration_not_open";
      else if (now >= openAt && closeAt && now < closeAt)
        uiState = "registration_open";
      else if (now >= openAt && (!closeAt || now >= closeAt))
        uiState = "registration_closed";
      else uiState = "registration_not_open";
    }
  } else {
    if (!openAt) uiState = "registration_not_open";
    else if (now < openAt) uiState = "registration_not_open";
    else if (now >= openAt && closeAt && now < closeAt)
      uiState = "registration_open";
    else uiState = "registration_closed";
  }

  const hasRated =
    Array.isArray(feedbacks) && feedbacks.some((r) => r.user === user?._id);

  // RENDER

  const handleShare = (activityId) => {
    const activityLink = `${window.location.origin}/activities/categories/${activityId}`;

    // التحقق من دعم ميزة المشاركة في المتصفح
    if (navigator.share) {
      navigator
        .share({
          title: "Check out this activity!",
          url: activityLink, // الرابط الذي سيتم مشاركته
        })
        .then(() => {
          console.log("Activity shared successfully");
        })
        .catch((error) => {
          console.error("Error sharing activity:", error);
        });
    } else {
      setPopup({
        show: true,
        type: "info",
        message: "Sharing not supported on this browser.",
      });
    }
  };

  const cancelRegistration = async (activityId) => {
    try {
      await cancelRegistrationAPI(activityId);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to cancel registration",
      };
    }
  };

  const EditMood = data?.status === "PENDING" ? true : false;

  return (
    <>
      <div
        style={{ marginTop: "75px" }}
        className="activity-details-page container mb-5 pt-1"
      >
        {/* top actions */}
        <div className="d-flex justify-content-between align-items-center mt-4 mb-3"></div>

        {/* HERO */}
        {/* <div className="activity-hero rounded-4 overflow-hidden shadow-sm mb-4 position-relative">
          <img
            src={
              status === "CANCELLED" ? "/Errors/cancelled.png" : image || poster
            }
            alt={title}
            className="w-100 hero-img"
          />
          <div className="hero-meta p-3 d-flex justify-content-between align-items-start">
            <div>
              <span className="hero-type-badge me-2">{type}</span>

              <h2 className="hero-title mb-1">{title}</h2>

              <div className="hero-location">
                {loc || "Location not specified"}
              </div>
            </div>
            <div className="text-end">
              <div className="fs-5 fw-bold">
                {registered}/{capacity}
              </div>
              <div className="small text-muted">Registered</div>
              <div className="mt-2">
                <span className={`status-pill ${status.toLowerCase()}`}>
                  {status}
                </span>
              </div>
            </div>
          </div>
        </div> */}
        <div className="activity-hero rounded-4 overflow-hidden shadow-sm mb-2 position-relative">
          <img
            src={
              status === "CANCELLED" ? "/Errors/cancelled.png" : image || poster
            }
            alt={title}
            className="w-100 hero-img"
          />

          <div className="hero-meta d-flex justify-content-between align-items-start">
            <div>
              <span className="hero-type-badge me-2">{type}</span>

              {/* DESKTOP TITLE */}
              <h2 className="hero-title hero-title-desktop mb-1">{title}</h2>

              <div className="hero-location">
                {loc || "Location not specified"}
              </div>
            </div>
            <div className="mt-2 STATUS">
              <span className={`status-pill ${status.toLowerCase()}`}>
                {status}
              </span>
            </div>

            {/* DESKTOP COUNTER */}
            <div className="text-end hero-counter">
              <div className="fs-5 fw-bold">
                {registered}/{capacity}
              </div>
              <div className="small">Registered</div>
              <div className="mt-2">
                <span className={`status-pill ${status.toLowerCase()}`}>
                  {status}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* MOBILE TITLE (outside hero) */}
        <h2 className="hero-title-mobile">{title}</h2>

        {/* grid */}
        <div className="row g-4">
          <div className="col-12 col-lg-8">
            {/* dates + countdown chips */}
            <div className="d-flex flex-wrap gap-2 mb-3">
              <div className="chip">
                <CalendarDays size={16} className="me-1" /> Start:{" "}
                {startAt ? startAt.toLocaleString() : "-"}
              </div>
              <div className="chip">
                <Clock size={16} className="me-1" /> End:{" "}
                {endAt ? endAt.toLocaleString() : "-"}
              </div>
              {regMessage && (
                <div className={`chip chip-${regMessage.type}`}>
                  {regMessage.text}
                </div>
              )}
              {activityMessage && (
                <div className={`chip chip-${activityMessage.type}`}>
                  {activityMessage.text}
                </div>
              )}
            </div>

            {/* location + supervisor */}
            <div className="d-flex flex-wrap gap-3 align-items-center mb-3 text-secondary small">
              {loc && (
                <div
                  className="d-inline-flex align-items-center gap-1 location-link"
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                  title="Press to view the location on the map"
                  onClick={() => handleOpenMap(coordinates[0], coordinates[1])}
                >
                  <MapPin size={16} /> {loc}
                </div>
              )}
              {supervisor && (
                <div className="d-flex align-items-center gap-2">
                  <User size={16} /> {supervisor}
                </div>
              )}
            </div>

            {/* progress */}
            <div className="mb-3">
              <div className="d-flex justify-content-between small text-secondary mb-1">
                <div>
                  <Users size={14} className="me-1" /> {registered} / {capacity}{" "}
                  Registered
                </div>
                <div className="fw-semibold">{percentage}%</div>
              </div>
              <div className="progress" style={{ height: "12px" }}>
                <div
                  className={`progress-bar ${isFull ? "bg-danger" : "bg-primary"}`}
                  role="progressbar"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              {isFull && (
                <p className="text-danger small fw-semibold mt-2">
                  ⚠️ This activity is fully booked. Registration is closed.
                </p>
              )}
            </div>

            {/* about */}
            <div className="card mb-3 border-0 shadow-sm">
              <div className="card-body">
                <h5 className="mb-3">About this Activity</h5>
                <p className="mb-0 text-muted">{description}</p>
              </div>
            </div>

            {/* instructions */}
            <div className="card mb-3 border-0 shadow-sm special-instructions">
              <div className="card-body d-flex gap-3 align-items-start">
                <AlertCircle size={20} className="text-primary mt-1" />
                <div>
                  <h6 className="mb-1">Special Instructions</h6>
                  <div className="text-muted small">
                    {instructions || "No special instructions provided."}
                  </div>
                </div>
              </div>
            </div>

            {/* links */}
            {links?.length > 0 && (
              <div className="card mb-3 border-0 shadow-sm">
                <div className="card-body">
                  <h6 className="mb-2">Useful Links</h6>
                  <div className="d-flex flex-column gap-2">
                    {links.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className="link-item"
                      >
                        🔗 {link.title}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Feedbacks area is unchanged — left for your EvaluateModal to handle */}
          </div>

          {/* actions panel (preserve all buttons/logic) */}
          <div className="col-12 col-lg-4">
            <div className="card border-0 shadow-sm p-3 actions-card">
              <h6 className="fw-bold mb-3">Quick Actions</h6>
              <div className="d-flex flex-column gap-2">
                {/* ================= STUDENT ================= */}
                {role === userRoles.student && (
                  <>
                    {/* ACTIVITY STARTED */}
                    {uiState === "activity_started" &&
                      (participants.includes(user?._id) ? (
                        attendance.includes(user?._id) ? (
                          <button className="btn btn-success w-100" disabled>
                            <Check size={16} /> Attendance Confirmed
                          </button>
                        ) : (
                          <button
                            className="btn btn-primary w-100"
                            onClick={() => setShowModal(true)}
                          >
                            <QrCode size={16} /> Confirm Attendance
                          </button>
                        )
                      ) : (
                        <button className="btn btn-secondary w-100" disabled>
                          You are not participating in this activity
                        </button>
                      ))}

                    {/* ACTIVITY FINISHED */}
                    {uiState === "activity_finished" &&
                      (attendance.includes(user?._id) ? (
                        hasRated ? (
                          <button className="btn btn-success w-100" disabled>
                            You already rated this activity
                          </button>
                        ) : (
                          <button
                            className="btn btn-primary w-100"
                            onClick={() => setShowEvaluateModal(true)}
                          >
                            Rate Activity
                          </button>
                        )
                      ) : (
                        <button className="btn btn-secondary w-100" disabled>
                          You did not attend this activity
                        </button>
                      ))}

                    {/* REGISTRATION NOT OPEN */}
                    {uiState === "registration_not_open" && (
                      <button className="btn btn-warning w-100" disabled>
                        Registration Not Open Yet
                      </button>
                    )}

                    {/* REGISTRATION OPEN */}
                    {uiState === "registration_open" &&
                      (!isRegistered ? (
                        !isFull ? (
                          <button
                            className={`btn w-100 ${
                              isBanned ? "btn-danger" : "btn-primary"
                            }`}
                            onClick={isBanned ? undefined : handleRegister}
                            disabled={isBanned}
                          >
                            {isBanned
                              ? "You are banned from this activity"
                              : "Register Now"}
                          </button>
                        ) : (
                          <button className="btn btn-secondary w-100" disabled>
                            Full Capacity
                          </button>
                        )
                      ) : (
                        <>
                          <button className="btn btn-success w-100" disabled>
                            You are registered — wait for activity to start
                          </button>
                          <button
                            onClick={() => setShowCancelModal(true)}
                            className="btn btn-danger w-100"
                          >
                            Cancel registration
                          </button>
                        </>
                      ))}

                    {/* REGISTRATION CLOSED */}
                    {uiState === "registration_closed" && (
                      <button className="btn btn-secondary w-100" disabled>
                        Registration Closed
                      </button>
                    )}
                  </>
                )}

                {/* ================= SUPERVISOR ================= */}
                {role === userRoles.supervisor && (
                  <>
                    {isOwner ? (
                      <>
                        <button
                          className="btn btn-outline-primary w-100 mb-2"
                          onClick={() =>
                            navigate(
                              `/activities/categories/${type}/participants/${id}`,
                              { state: { id } },
                            )
                          }
                        >
                          <FaUsers size={16} /> View Participants
                        </button>

                        {EditMood && (
                          <button
                            className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-2"
                            onClick={handleEdit}
                          >
                            <Edit size={16} /> Edit
                          </button>
                        )}
                        <button className="btn btn-primary w-100 mb-2">
                          <QrCode size={16} /> Attendance:{" "}
                          <span className="text-bg-light text-primary px-2 rounded-2">
                            {attendanceOTP}
                          </span>
                        </button>
                      </>
                    ) : (
                      <>
                        {/* ACTIVITY STARTED */}
                        {uiState === "activity_started" &&
                          (participants.includes(user?._id) ? (
                            attendance.includes(user?._id) ? (
                              <button className="btn btn-success w-100 mb-2">
                                <Check size={16} /> Attendance Confirmed
                              </button>
                            ) : (
                              <button
                                className="btn btn-primary w-100 mb-2"
                                onClick={() => setShowModal(true)}
                              >
                                <QrCode size={16} /> Confirm Attendance Code
                              </button>
                            )
                          ) : (
                            <button
                              className="btn btn-warning w-100 mb-2"
                              disabled
                            >
                              You are not participating
                            </button>
                          ))}

                        {/* ACTIVITY FINISHED */}
                        {uiState === "activity_finished" && (
                          <button
                            className="btn btn-secondary w-100 mb-2"
                            disabled
                          >
                            Activity Finished
                          </button>
                        )}

                        {/* REGISTRATION NOT OPEN */}
                        {uiState === "registration_not_open" && (
                          <button
                            className="btn btn-warning w-100 mb-2"
                            disabled
                          >
                            Registration Not Open Yet
                          </button>
                        )}

                        {/* REGISTRATION OPEN */}
                        {uiState === "registration_open" &&
                          (!isRegistered ? (
                            !isFull ? (
                              <button
                                className={`btn w-100 ${
                                  isBanned ? "btn-danger" : "btn-primary"
                                }`}
                                onClick={isBanned ? undefined : handleRegister}
                                disabled={isBanned}
                              >
                                {isBanned
                                  ? "You are banned from this activity"
                                  : "Register Now"}
                              </button>
                            ) : (
                              <button
                                className="btn btn-secondary w-100 mb-2"
                                disabled
                              >
                                Full Capacity
                              </button>
                            )
                          ) : (
                            <>
                              <button
                                className="btn btn-success w-100"
                                disabled
                              >
                                You are registered — wait for activity to start
                              </button>
                              <button
                                onClick={() => setShowCancelModal(true)}
                                className="btn btn-danger w-100"
                              >
                                Cancel registration
                              </button>
                            </>
                          ))}

                        {/* REGISTRATION CLOSED */}
                        {uiState === "registration_closed" && (
                          <button
                            className="btn btn-secondary w-100 mb-2"
                            disabled
                          >
                            Registration Closed
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* ================= ADMIN ================= */}
                {role === userRoles.admin && (
                  <>
                    {/* PENDING */}
                    {status === "PENDING" && (
                      <div className="d-flex flex-column gap-2">
                        <button
                          className="btn btn-success w-100"
                          onClick={() => handleAccept(id)}
                        >
                          Accept
                        </button>
                        <button
                          className="btn btn-danger w-100"
                          onClick={() => handleReject(id)}
                        >
                          Reject
                        </button>
                        <button
                          className="btn btn-outline-primary w-100"
                          onClick={handleEdit}
                        >
                          <Edit size={16} /> Edit
                        </button>
                      </div>
                    )}

                    {/* ACTIVE */}
                    {status === "ACTIVE" && (
                      <>
                        <button
                          className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-2"
                          onClick={handleEdit}
                        >
                          <Edit size={16} /> Edit
                        </button>
                        {/* <button
                          className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2"
                          onClick={handleDelete}
                        >
                          <Trash2 size={16} /> Delete
                        </button> */}
                        <button
                          className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-2"
                          onClick={() =>
                            navigate(
                              `/activities/categories/${type}/participants/${id}`,
                              { state: { id } },
                            )
                          }
                        >
                          <FaUsers size={16} /> View Participants
                        </button>
                      </>
                    )}

                    {/* FINISHED */}
                    {status === "FINISHED" && (
                      <button
                        className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-2"
                        onClick={() =>
                          navigate(
                            `/activities/categories/${type}/participants/${id}`,
                            { state: { id } },
                          )
                        }
                      >
                        <FaUsers size={16} /> View Participants
                      </button>
                    )}

                    {/* CANCELLED */}
                    {status === "CANCELLED" && (
                      <>
                        <button
                          className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2"
                          onClick={handleDelete}
                        >
                          <Trash2 size={16} /> Delete
                        </button>
                        <button
                          className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-2"
                          onClick={() =>
                            navigate(
                              `/activities/categories/${type}/participants/${id}`,
                              { state: { id } },
                            )
                          }
                        >
                          <FaUsers size={16} /> View Participants
                        </button>
                      </>
                    )}
                  </>
                )}

                {/* ---------- زر المشاركة ---------- */}
                <button
                  className="btn btn-outline-info w-100 mt-2 d-flex align-items-center justify-content-center gap-2"
                  onClick={() => handleShare(id)} // تحتاج لتعريف دالة المشاركة
                >
                  <Share2 size={16} /> Share Activity
                </button>

                <button
                  className="btn btn-outline-secondary w-100 mt-2"
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeft size={16} className="me-1" /> Back
                </button>
              </div>
            </div>
          </div>
        </div>

        {adminPayload && <AdminTargetInfo adminPayload={adminPayload} />}
        <ActivityFeedback analytics={feedbackAnalytics} />
      </div>

      <ChatbotButton />
      <ConfirmAttendanceModal
        show={showModal}
        onClose={() => setShowModal(false)}
        activityId={id}
        activityName={title}
        onSubmit={sendOTP}
      />
      <ConfirmCancelRegistrationModal
        show={ShowCancelModal}
        onClose={() => setShowCancelModal(false)}
        activityId={id}
        activityName={title}
        onConfirm={cancelRegistration}
      />

      <ConfirmRegistrationModal
        show={show}
        handleClose={() => setShow(false)}
        handleConfirm={handleConfirm}
      />
      <EvaluateModal
        show={showEvaluateModal}
        onClose={() => setShowEvaluateModal(false)}
        onSubmit={handleEvaluateSubmit}
        loading={submittingEval}
        message={evalMessage}
        messageType={evalMessageType}
      />

      {/* === Confirmation Modal === */}
      {modalConfig.show && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1050,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            className="bg-white p-4 rounded-4 shadow-lg text-center"
            style={{
              maxWidth: "400px",
              width: "90%",
              animation: "fadeInUp 0.3s ease",
            }}
          >
            {/* Icon */}
            <div
              className={`mx-auto rounded-circle d-flex align-items-center justify-content-center mb-3 ${
                modalConfig.type === "approve"
                  ? "bg-success bg-opacity-10 text-success"
                  : "bg-danger bg-opacity-10 text-danger"
              }`}
              style={{ width: "70px", height: "70px" }}
            >
              {modalConfig.type === "approve" ? (
                <CheckCircle size={35} />
              ) : (
                <XCircle size={35} />
              )}
            </div>

            {/* Text */}
            <h4 className="fw-bold text-dark mb-2">
              {modalConfig.type === "approve"
                ? "Approve Activity?"
                : "Reject Activity?"}
            </h4>
            <p className="text-muted mb-4 small">
              {modalConfig.type === "approve"
                ? "Are you sure you want to approve this activity? It will become visible to students."
                : "Are you sure you want to reject this activity? This action cannot be undone."}
            </p>

            {/* Buttons */}
            <div className="d-flex gap-2">
              <button
                className="btn btn-light flex-grow-1 fw-bold py-2 rounded-pill"
                onClick={closeModal}
                disabled={actionLoading}
              >
                Cancel
              </button>

              <button
                className={`btn flex-grow-1 fw-bold py-2 rounded-pill d-flex align-items-center justify-content-center gap-2 text-white ${
                  modalConfig.type === "approve" ? "btn-success" : "btn-danger"
                }`}
                onClick={executeAction}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <>
                    <Loader2
                      size={18}
                      className="spinner-border spinner-border-sm me-1"
                    />{" "}
                    Processing...
                  </>
                ) : modalConfig.type === "approve" ? (
                  "Yes, Approve"
                ) : (
                  "Yes, Reject"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {popup.show && (
        <PopupMessage
          type={popup.type}
          message={popup.message}
          onClose={() => setPopup({ ...popup, show: false })}
        />
      )}
    </>
  );
}
